import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ErrorBoundary from "../AppComponents/AppErrorBoundary";
import BlankLayout from "../Components/Layouts/BlankLayout";
import ProtectedLayout from "../Components/Layouts/ProtectedLayout";
import BroadcastLogsTable from "../Components/UiComponents/BroadcastLogsTable";
import WhatsappBroadcastHistoryLogs from "../Components/Broadcast/WhatsApp/ScheduledBroadcast/BroadcastLogsTable";
import WhatsappBroadcastDashboardSummary from "../Components/Analytics/Insights/Logs/Overview/WhatsappBroadcastDashboardSummary";
import RouteGuard from "./RouteGuard";

import AppStore from "../Components/AppStore/AppStore";
import AppDetail from "../Components/AppStore/AppDetail/AppDetail";
import AppConfig from "../Components/AppStore/AppsConfig";
import RedirectPage from "../Components/AppStore/RedirectPage";

import OrderList from "../Components/Leads/contactDetailedComponents/SubPages/Ecommerce/OrderList";
import { getRestrictedMenuOptions } from "../Utils/rolesUtils";
import { useAppSelector } from "../Store";
import { useAuthStore } from "../Store/Slices/AuthSlice";
import ShopifyRedirectPage from "../Components/AppStore/ShopifyRedirect";

//CONSTANTS
import {ContactSideBarMenu, ContactSideBarMenuPrivate} from "../Components/Leads/contactDetailedComponents/Sidebar/ContactSidebar";
import APIKeyGeneration from "../Components/API_key_generation";
import FileManagerHeader from "../Components/FileManager/fileManagerHeader";
import BoardUploadFile from "../Components/FileManager/boardUploadFile";
import CreateApi from "../Components/ApiLibrary/pages/CreateApi";
import ApiLibraryList from "../Components/ApiLibrary/pages/ApiList";

// LAZY COMPONENTS

const WhatsappAutomation = lazy(
  () => import("../Components/Broadcast/WhatsApp/Automation/WhatsappAutomation")
);
const WhatsappBroadcastHistory = lazy(
  () =>
    import(
      "../Components/Broadcast/WhatsApp/BroadcastHistory/WhatsappBroadcastHistory"
    )
);
const WhatsappDetails = lazy(
  () =>
    import("../Components/Broadcast/WhatsApp/BroadcastHistory/WhatsappDetails")
);
const WhatsappPreferences = lazy(
  () =>
    import("../Components/Broadcast/WhatsApp/Preferences/WhatsappPreferences")
);
const BroadcastCreate = lazy(
  () =>
    import(
      "../Components/Broadcast/WhatsApp/ScheduledBroadcast/BroadcastCreate"
    )
);
const WhatsappScheduledBroadcast = lazy(
  () =>
    import(
      "../Components/Broadcast/WhatsApp/ScheduledBroadcast/WhatsappScheduledBroadcast"
    )
);
const TemplateCreate = lazy(
  () => import("../Components/Broadcast/WhatsApp/Templates/TemplateCreate")
);
const TemplatesListing = lazy(
  () => import("../Components/Broadcast/WhatsApp/Templates/TemplatesListing")
);
const ContactMarketingImportDeatails = lazy(
  () =>
    import("../Components/ContactMarketingList/ContactMarketingImportDeatails")
);
const ContactMarketingImport = lazy(
  () =>
    import("../Components/ContactMarketingList/ContactMarketingImportHistory")
);
const ContactMarketingList = lazy(
  () => import("../Components/ContactMarketingList/ContactMarketingList")
);
const BusinessHours = lazy(
  () =>
    import("../Components/Settings/ChatSettings/BusinessHours/BusinessHours")
);
const CannedResponse = lazy(
  () =>
    import("../Components/Settings/ChatSettings/CannedResponse/CannedResponse")
);
const ChatAssignment = lazy(
  () =>
    import("../Components/Settings/ChatSettings/ChatAssignment/ChatAssignment")
);
const InactivityTimeout = lazy(
  () =>
    import(
      "../Components/Settings/ChatSettings/InactivityTimeout/InactivityTimeout"
    )
);
const ChatLabels = lazy(
  () => import("../Components/Settings/ChatSettings/Labels/ChatLabels")
);
const CreateLifeCycle = lazy(
  () =>
    import(
      "../Components/Settings/DataManagement/LifecycleStage/CreateLifeCycle"
    )
);
const RolesPermissions = lazy(
  () =>
    import(
      "../Components/Settings/UsersAndPermissions/RolesPermissions/RolesPermissions"
    )
);
const AddUser = lazy(
  () => import("../Components/Settings/UsersAndPermissions/Users/AddUser")
);
const Users = lazy(
  () => import("../Components/Settings/UsersAndPermissions/Users/Users")
);
const EmailValidation = lazy(
  () => import("../Components/Welcome/EmailValidation")
);
const Home = lazy(() => import("../socket/screens/Home"));
const Automation = lazy(
  () => import("../Components/AutomationBuilder/Pages/Automation")
);
const AutomationBuilder = lazy(
  () => import("../Components/AutomationBuilder/Index")
);
const AutoAssignmentTabs = lazy(
  () =>
    import(
      "../Components/Settings/AutoAssignment/AutoAssignmentRules/AutoAssignmentTabs"
    )
);
const ContactsListing = lazy(
  () =>
    import(
      "../Components/Settings/AutoAssignment/AutoAssignmentRules/Contacts/ContactsListing"
    )
);
const CreateContacts = lazy(
  () =>
    import(
      "../Components/Settings/AutoAssignment/AutoAssignmentRules/Contacts/CreateContacts"
    )
);
const SidebarLayoutWrapper = lazy(
  () => import("../AppComponents/SidebarAccordion/SidebarLayoutWrapper")
);
const ContactsExportHistory = lazy(
  () => import("../Components/Leads/ContactsExportHistory")
);
const WelcomeWhatsappBroadcast = lazy(
  () => import("../Components/Broadcast/WelcomeWhatsappBroadcast")
);
const Agent = lazy(
  () => import("../Components/Dashboard/Agent_Dashboard/Agent")
);
const ParentBot = lazy(
  () => import("../Components/Dashboard/Bot_Dashboard/ParentBot")
);
const Summary = lazy(() => import("../Components/Dashboard/Summary"));
const Homepage = lazy(() => import("../Components/Homepage"));
const LeadsPage = lazy(() => import("../Components/Leads/LeadsPage"));
const SegmentListFn = lazy(() => import("../Components/Segment/SegmentList"));
const SegmentContactList = lazy(
  () => import("../Components/Segment/Segment_ContactList")
);
const Permissions = lazy(
  () =>
    import(
      "../Components/Settings/UsersAndPermissions/RolesPermissions/Tabs/Permission"
    )
);
const WallectInvoices = lazy(
  () => import("../Components/Subscription/Credit_List/WallectInvoiceList")
);
const Invoices = lazy(() => import("../Components/Subscription/InvoicesList"));
const PlanDetails = lazy(
  () => import("../Components/Subscription/PlanDetails")
);
const Plans = lazy(
  () => import("../Components/Subscription/SubscriptionPlanList")
);
const Subscription = lazy(
  () => import("../Components/Subscription/Subscriptionplan")
);
const SubscriptionSuccess = lazy(
  () => import("../Components/Subscription/SuccessSubscription")
);
const LeadsDetailsPage = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/SubPages/BasicInformation/LeadsDetailsPage"
    )
);
const LifecycleListing = lazy(
  () =>
    import(
      "../Components/Settings/DataManagement/LifecycleStage/LifecycleListing"
    )
);
const DependencyFiledList = lazy(
  () =>
    import(
      "../Components/Settings/ContactModule/Dependencies/DependencyFiledList"
    )
);
const ContactProperties = lazy(
  () => import("../Components/Settings/ContactModule/ContactProperties")
);
const LeadCommunicationStats = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/SubPages/LeadCommunicationStats"
    )
);
const LeadActivities = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/SubPages/Activities/LeadActivities"
    )
);
const HomeRouteIndex = lazy(() => import("../Components/HomeRouteIndex/Index"));
const LeadFiles = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/SubPages/Files/LeadFile"
    )
);
const LeadNotes = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/SubPages/Activities/LeadNotes"
    )
);
const LeadTask = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/SubPages/Activities/ContactTask"
    )
);
const AllTaskScreen = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/SubPages/Activities/AllTaskScreen"
    )
);
const ChatHistory = lazy(() => import("../socket/screens/ChatHistory"));
const MainDashboard = lazy(
  () => import("../Components/MainDashboard/MainDashboard")
);
const ApplicationSummaryDetails = lazy(
  () =>
    import(
      "../Components/MainDashboard/ApplicationSummaryDetails/ApplicationSummaryDetails"
    )
);
const WhatsAppBroadcastAnalyticsTopMenu = lazy(
  () =>
    import(
      "../Components/Broadcast/WhatsApp/BroadcastHistory/Analytics/WhatsAppBroadcastAnalyticsTopMenu"
    )
);

const WhatappBroadcastOverview = lazy(
  () =>
    import(
      "../Components/Broadcast/WhatsApp/BroadcastHistory/Analytics/WhatappOverview"
    )
);

const WhatsappBroadcastIndividualHistoryLogs = lazy(
  () =>
    import(
      "../Components/Broadcast/WhatsApp/BroadcastHistory/Analytics/WhatsappBroadcastIndividualHistoryLogs"
    )
);
const WhatsappManageProfile = lazy(
  () => import("../Components/Settings/ManageProfile/WhatsappManageProfile")
);
const Webhook = lazy(() => import("../Components/Settings/Webhook/Webhook"));

const UserProfile = lazy(
  () => import("../Components/Settings/Profile/index"),
)

//extra imports
const CommonTabHeader = lazy(
  () =>
    import(
      "../Components/Leads/contactDetailedComponents/Sidebar/CommonTabHeader"
    )
);

const BotBuilder = lazy(() => import("../Components/BotBuilder/BotBuilder"));
// const BotBuilder = lazy(() => {
//   return Promise.all([
//     import("../Components/BotBuilder/BotBuilder"),
//     new Promise(resolve => setTimeout(resolve, 665300))
//   ])
//   .then(([moduleExports]) => moduleExports);
// });
const BotSettings = lazy(
  () => import("../Components/BotBuilder/BotSettings/BotSettings")
);
const AutoReplies = lazy(
  () =>
    import(
      "../Components/BotBuilder/BotSettings/Conversations/AutoReplies/AutoReplies"
    )
);

const EnabledDevices = lazy(
  () =>
    import(
      "../Components/BotBuilder/BotSettings/Conversations/EnabledDevices/EnabledDevices"
    )
);

const PopoutMessage = lazy(
  () =>
    import(
      "../Components/BotBuilder/Customize/Appearence/Callout/PopoutMessage"
    )
);
const LookFeel = lazy(
  () => import("../Components/BotBuilder/Customize/Appearence/LookFeel")
);
const BotKnowledge = lazy(
  () => import("../Components/BotBuilder/BotKnowledge/BotKnowledge")
);
const FaqIndex = lazy(
  () => import("../Components/BotBuilder/BotKnowledge/Faq/FaqIndex")
);
const NotAnsweredIndex = lazy(
  () =>
    import("../Components/BotBuilder/BotKnowledge/NotAnswered/NotAnsweredIndex")
);
const SmartAi = lazy(
  () => import("../Components/BotBuilder/BotKnowledge/SmartAi/SmartAi")
);
const DeployScripts = lazy(
  () => import("../Components/BotBuilder/DeploySettings/Scripts")
);
const FaqDetails = lazy(() => import("../Components/FAQ/FaqDetails"));
const FlowBuilderV2 = lazy(() => import("../Modules/FlowBuilder"));
const FaqPdf = lazy(
  () => import("../Components/BotBuilder/BotKnowledge/Faq/Pdf/FaqPdf")
);
const FaqSitemap = lazy(
  () => import("../Components/BotBuilder/BotKnowledge/Faq/Sitemap/FaqSitemap")
);
const FaqText = lazy(
  () => import("../Components/BotBuilder/BotKnowledge/Faq/Text/FaqText")
);
const FaqWebsite = lazy(
  () => import("../Components/BotBuilder/BotKnowledge/Faq/Website/FaqWebsite")
);
const AnsweredFaq = lazy(
  () =>
    import(
      "../Components/BotBuilder/BotKnowledge/NotAnswered/FaqAnswered/AnsweredFaq"
    )
);
const NotAnswered = lazy(() => import("../Components/FAQ/NotAnswered"));
const FreshStartPage = lazy(
  () => import("../Components/MyChatbots/FreshStartPage")
);
const AllChatbots = lazy(() => import("../Components/MyChatbots/AllChatbots"));
const CreateBotForm = lazy(
  () => import("../Components/MyChatbots/CreateBots/CreateBotForm")
);
const FaqBuilder = lazy(() => import("../Components/FAQ/FaqBuilder"));
const LinkAndQRCode = lazy(
  () =>
    import(
      "../Components/BotBuilder/Customize/Appearence/LinkAndQRCode/LinkAndQRCode"
    )
);

const PersistantMenu = lazy(
  () => import("../Components/BotBuilder/Customize/Appearence/PersistantMenu")
);

const CreateWorkspace = lazy(
  () => import("../Components/BotBuilder/workspace/CreateWorkspace")
);

const WhatsappOnboard = lazy(() => import("../Components/whatsapp-onboarding"));
const IntroScreen = lazy(
  () => import("../Components/whatsapp-onboarding/intro-screen")
);
const OnboardStepperForm = lazy(
  () => import("../Components/whatsapp-onboarding/onboard-stepper-form")
);
const TrackingExpenses = lazy(
  () => import("../Components/Subscription/TrackingExpenses/TrackingExpenses")
);
const BillingSummary = lazy(
  () => import("../Components/Subscription/Biling-summary")
);
const WhatsappBillingLogs = lazy(
  () => import("../Components/Subscription/Biling-summary/WhatsappBillingLogs")
);
const WhatsappTemplateAnalytics = lazy(
  () =>
    import(
      "../Components/Broadcast/WhatsApp/Templates/WhatsappTemplateAnalytics/WhatsappTemplateAnalytics"
    )
);

const FlowBuilderList = lazy(() => import("../Whatsapp/src/flowBuilderList"));
const FlowCreateScreen = lazy(() => import("../Whatsapp/src/flowCreateScreen"));
const WhatsAppWapper = lazy(() => import("../Whatsapp/src/whatsAppWapper"));
const ProtectedAccessDeniedPage = lazy(
  () => import("../AppComponents/ProtectedComponents/AccessDenied/index")
);
const App404 = lazy(() => import("../AppComponents/App404"));
const OptInManagement = lazy(
  () =>
    import(
      "../Components/Settings/ChatSettings/Opt-inManagement/opt-in-Management"
    )
);
const DefaultTemplate = lazy(
  () => import("../Components/Broadcast/WhatsApp/Templates/DefaultTemplate")
);

const CTWASetup = lazy(() => import("../Components/WhatsappAds/CTWA/setup"));
const CTWAAdsList = lazy(
  () => import("../Components/WhatsappAds/CTWA/adsListing")
);
const TaskOutcome = lazy(
  () =>
    import("../Components/Settings/DataManagement/LifecycleStage/TaskOutcome")
);
const Notification = lazy(
  () => import("../Components/Settings/Notification/index"),
);
const CatalogSetup = lazy(
  () => import("../Components/Catalog/setup"),
);

// catalogue lazy loaded components

const CatalogueListing = lazy(
  () =>
    import(
      "../Components/Catalog/CatalogueListing/CatalogueListing"
    )
);
const CatalogueProducts = lazy(
  () =>
    import(
      "../Components/Catalog/CatalogueListing/CatalogueProducts"
    )
);

const CatalogueCollections = lazy(
  () =>
    import(
      "../Components/Catalog/CatalogueListing/Catalogues"
    )
);

const Catalogues = lazy(
  () =>
    import(
      "../Components/Catalog/CatalogueListing/CatalogueCollections"
    )
);
const SubAccountsLiting = lazy(() => import("../Components/SubAccount"));

const TransactionalLogs = lazy(() => import("../Components/SubAccount/TransactionalLogs"));

const OwnTransactionalLogs = lazy(() => import("../Components/SubAccount/OwnTransactionalLogs"));

const ProtectedRoutes = () => {
  const { userConfig } = useAppSelector(useAuthStore);
  const ContactSideBarMenuData =  Array.from(
    new Set([
      ...ContactSideBarMenu,
      ...getRestrictedMenuOptions(ContactSideBarMenuPrivate, userConfig?.payload)
    ])
  );
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={<ProtectedLayout />}>
          {/*************** ONBOARDING ************** */}
          <Route path="/onboarding" element={<WhatsappOnboard />}>
            <Route path="welcome" element={<IntroScreen />} />
            <Route path=":id" element={<OnboardStepperForm />} />
          </Route>

          {/*************** Homepage ************** */}
          <Route path="/" element={<Homepage />} />

          {/*************** ANALYTICS ************** */}
          <Route
            path="/analytics"
            element={
              <RouteGuard
                moduleId="analytics"
                element={<BlankLayout documentTitle="Analytics" />}
              />
            }
          >
            <Route
              path="dashboard"
              element={
                <RouteGuard
                  moduleId="conversation_summary"
                  element={<MainDashboard />}
                />
              }
            />

            {/* Rout For Lcoal storage */}

            <Route
              path="dashboard/:id"
              element={
                <RouteGuard
                  moduleId="conversation_summary"
                  element={<ApplicationSummaryDetails />}
                />
              }
            />

            <Route
              path="dashboard-summary"
              element={
                <RouteGuard
                  moduleId="conversation_summary"
                  element={<Summary />}
                />
              }
            />
            <Route
              path="dashboard-agent"
              element={
                <RouteGuard moduleId="agent_summary" element={<Agent />} />
              }
            />
            <Route
              path="dashboard-bot"
              element={
                <RouteGuard moduleId="bot_summary" element={<ParentBot />} />
              }
            />
          </Route>

          {/*************** CONVERSATION INBOX ************** */}
          <Route
            path="/conversations"
            element={
              <RouteGuard
                moduleId="conversation_inbox"
                element={<ChatHistory />}
              />
            }
          >
            <Route path="chats" element={<Home />} />
          </Route>

          {/*************** CONTACTS ************** */}
          <Route
            path="/contacts"
            element={
              <RouteGuard
                moduleId="contacts"
                element={
                  <BlankLayout layoutWithDiv={true} documentTitle="Contacts" />
                }
              />
            }
          >
            <Route
              path="list"
              element={
                <RouteGuard moduleId="contacts" element={<LeadsPage />} />
              }
            />
            <Route
              path="list/:view_id"
              element={
                <RouteGuard moduleId="contacts" element={<LeadsPage />} />
              }
            />
            <Route
              path="view/:contact_id"
              element={
                <RouteGuard
                  moduleId="contacts"
                  element={
                    <SidebarLayoutWrapper
                      sidebarMenuOptions={ContactSideBarMenuData}
                      menuDisplayType={"menu"}
                      sidebarTitle="Contact Details"
                      outletCommonHeader={<CommonTabHeader />}
                      dynamicRouteSlug="contact_id"
                      layoutWithDiv={true}
                      documentTitle="Contact"
                    />
                  }
                />
              }
            >
              <Route path="basic-information" element={<LeadsDetailsPage />} />
              <Route
                path="communication-stats"
                element={<LeadCommunicationStats />}
              />
              <Route path="files" element={<LeadFiles />} />
              <Route path="activities" element={<LeadActivities />}>
                <Route path="tasks/:id?" element={<LeadTask />} />
                <Route path="notes" element={<LeadNotes />} />
              </Route>
              <Route path=":store_type/order-details" element={<OrderList />} />
            </Route>

            <Route
              path="segment"
              element={
                <RouteGuard moduleId="segments" element={<BlankLayout />} />
              }
            >
              <Route path="list" element={<SegmentListFn />} />
              <Route path="list/:seg_id" element={<SegmentContactList />} />
            </Route>

            <Route
              path="marketing"
              element={
                <RouteGuard
                  moduleId="marketing_list"
                  element={<BlankLayout />}
                />
              }
            >
              <Route path="list" element={<ContactMarketingList />} />
              <Route
                path="detail/:id"
                element={<ContactMarketingImportDeatails />}
              />
            </Route>
          </Route>
          <Route path="/file-manager" element={<FileManagerHeader />} />
          <Route path="/board-upload/:id" element={<BoardUploadFile />} />
          {/*************** BROADCAST ************** */}
          <Route
            path="/broadcast"
            element={
              <RouteGuard
                moduleId="broadcast"
                element={<BlankLayout documentTitle="Whatsapp" />}
              />
            }
          >
            {/*************** BROADCAST /WHATSAPP ************** */}
            <Route
              path="whatsapp"
              element={
                <RouteGuard
                  moduleId="whatsapp_broadcast"
                  element={<BlankLayout documentTitle="Broadcast" />}
                />
              }
            >
              <Route
                index
                element={
                  <HomeRouteIndex
                    dynamicMenuKeyName="whatsapp_broadcast_sidebar_menu"
                    routeIndexPath="/broadcast/whatsapp"
                  />
                }
              />

              <Route path="start" element={<WelcomeWhatsappBroadcast />} />
              <Route
                path="history"
                element={
                  <RouteGuard
                    moduleId="whatsapp_broadcast_crud"
                    element={<WhatsappBroadcastHistory />}
                  />
                }
              />
              <Route
                path="scheduled"
                element={
                  <RouteGuard
                    moduleId="whatsapp_broadcast_crud"
                    element={<WhatsappScheduledBroadcast />}
                  />
                }
              />
              <Route
                path="logs"
                element={
                  <RouteGuard
                    moduleId="whatsapp_broadcast_crud"
                    element={<WhatsappBroadcastHistoryLogs />}
                  />
                }
              />
              <Route
                path="templates"
                element={
                  <RouteGuard
                    moduleId="whatsapp_template"
                    element={<DefaultTemplate />}
                  />
                }
              />
              <Route
                path="default_template"
                element={
                  <RouteGuard
                    moduleId="whatsapp_template"
                    element={<DefaultTemplate />}
                  />
                }
              />
              <Route
                path="templates/:id"
                element={
                  <RouteGuard
                    moduleId="whatsapp_template"
                    element={<WhatsappTemplateAnalytics />}
                  />
                }
              />
              <Route
                path="preferences"
                element={
                  <RouteGuard
                    moduleId="whatsapp_preferences"
                    element={<WhatsappPreferences />}
                  />
                }
              />
              <Route
                path="automation"
                element={
                  <RouteGuard
                    moduleId="whatsapp_automation"
                    element={<WhatsappAutomation />}
                  />
                }
              />

              <Route
                path="broadcast-details/:broadcast_id"
                element={
                  <RouteGuard
                    moduleId="whatsapp_broadcast_crud"
                    element={<WhatsAppBroadcastAnalyticsTopMenu />}
                  />
                }
              >
                <Route path="overview" element={<WhatappBroadcastOverview />} />
                <Route
                  path="logs"
                  element={<WhatsappBroadcastIndividualHistoryLogs />}
                />
              </Route>

              <Route
                path="create-broadcast/:step_id"
                element={
                  <RouteGuard
                    moduleId="whatsapp_broadcast_crud"
                    element={<BroadcastCreate />}
                  />
                }
              />
              <Route
                path="edit-broadcast/:step_id/:broadcast_id"
                element={
                  <RouteGuard
                    moduleId="whatsapp_broadcast_crud"
                    element={<BroadcastCreate />}
                  />
                }
              />
              <Route
                path="create-template"
                element={
                  <RouteGuard
                    moduleId="whatsapp_template"
                    element={<TemplateCreate />}
                  />
                }
              />
              <Route
                path="edit-template/:template_id"
                element={
                  <RouteGuard
                    moduleId="whatsapp_template"
                    element={<TemplateCreate />}
                  />
                }
              />
              <Route
                path="view-template/:template_id"
                element={
                  <RouteGuard
                    moduleId="whatsapp_template"
                    element={<TemplateCreate />}
                  />
                }
              />
            </Route>
          </Route>

          {/* Catalogue Listing Routes */}
          <Route
            path="/catalogue"
            element={
              <RouteGuard
                moduleId="whatsapp_broadcast"
                element={<BlankLayout />}
              />
            }
          >
              <Route path="setup" element={<CatalogSetup />} />
              <Route path="list" element={<CatalogueListing />}>
              <Route path="products" element={<CatalogueProducts />} />
              <Route path="collections" element={<CatalogueCollections />} />
              <Route path="catalogues" element={<Catalogues />} />
            </Route>
          </Route>

          {/*************** Automation Builder ************** */}
          <Route
            path="/automation"
            element={
              <RouteGuard
                moduleId="automation"
                element={<BlankLayout documentTitle="Automation" />}
              />
            }
          >
            <Route path="lists" element={<Automation />} />
            <Route path="edit/:id" element={<AutomationBuilder />} />
            <Route path="view/:id" element={<AutomationBuilder />} />
          </Route>

          {/*************** Whatsapp Flow Builder ************** */}
          <Route
            path="/whatsapp/flow"
            element={
              <RouteGuard
                moduleId="whatsapp_flows"
                element={<BlankLayout documentTitle="Flows" />}
              />
            }
          >
            <Route path="" element={<FlowBuilderList />} />
            <Route path="create" element={<FlowCreateScreen />} />
            <Route path="builder/:id" element={<WhatsAppWapper />} />
            <Route path="edit/:id" element={<AutomationBuilder />} />
            <Route path="view/:id" element={<AutomationBuilder />} />
          </Route>
          {/*************** Whatsapp Flow Builder ************** */}

          {/*************** SETTINGS ************** */}
          <Route
            path="/settings"
            element={
              <RouteGuard
                moduleId="settings"
                element={
                  <SidebarLayoutWrapper
                    menuOptionsKeyName="setting_menu_options"
                    menuDisplayType={"accordion"}
                    documentTitle="Settings"
                  />
                }
              />
            }
          >
            <Route
              index
              element={
                <HomeRouteIndex
                  dynamicMenuKeyName="setting_menu_options"
                  routeIndexPath="/settings"
                />
              }
            />
            {/*************** SETTINGS / API Generation ************** */}
            <Route path="api-generation" element={<APIKeyGeneration />} />
            {/*************** SETTINGS / USERS AND PERMISSIONS ************** */}
            <Route
              path="users-and-permissions"
              element={
                <RouteGuard
                  moduleId="users_and_permissions"
                  element={<BlankLayout documentTitle="Users & Permissions" />}
                />
              }
            >
              <Route
                path="users"
                element={
                  <RouteGuard
                    moduleId="manage_users"
                    element={<BlankLayout />}
                  />
                }
              >
                <Route path="list" element={<Users />} />
                <Route path="add-user" element={<AddUser />} />
                <Route path="edit-user/:id" element={<AddUser />} />
              </Route>


              <Route
                path="roles"
                element={
                  <RouteGuard
                    moduleId="manage_roles"
                    element={<BlankLayout />}
                  />
                }
              >
                <Route path="list" element={<RolesPermissions />} />
                <Route path="list/create" element={<Permissions />} />
                <Route path="list/:id" element={<Permissions />} />
              </Route>
            </Route>
            {/*************** SETTINGS / NOTIFICATION ************** */}
            <Route
              path="notifications"
              element={
                <RouteGuard
                  moduleId="notifications"
                  element={<BlankLayout />}
                />
              }
            >
              <Route
                path="general"
                element={
                  <RouteGuard
                    moduleId="general_notifications"
                    element={<Notification />}
                  />
                }
              />
            </Route>
            {/*************** SETTINGS / DATA MANAGEMENT ************** */}
            <Route
              path="data-management"
              element={
                <RouteGuard
                  moduleId="data_management"
                  element={<BlankLayout documentTitle="Data management" />}
                />
              }
            >
              <Route
                path="import-history"
                element={
                  <RouteGuard
                    moduleId="import_history"
                    element={<ContactMarketingImport />}
                  />
                }
              />
              <Route
                path="export-contact-history"
                element={
                  <RouteGuard
                    moduleId="export_history"
                    element={<ContactsExportHistory />}
                  />
                }
              />
              <Route
                path="lifecycle"
                element={
                  <RouteGuard
                    moduleId="manage_lifecycle"
                    element={<LifecycleListing />}
                  />
                }
              />
              <Route
                path="task-outcome"
                element={
                  <RouteGuard
                    moduleId="task_outcome"
                    element={<TaskOutcome />}
                  />
                }
              />

              <Route
                path="contact-properties"
                element={
                  <RouteGuard
                    moduleId="manage_contact_properties"
                    element={<BlankLayout />}
                  />
                }
              >
                <Route path="list" element={<ContactProperties />} />
                <Route
                  path="field-dependency-list"
                  element={<DependencyFiledList />}
                />
              </Route>
              <Route
                path="lifecycle-stage"
                element={
                  <RouteGuard
                    moduleId="manage_lifecycle"
                    element={<BlankLayout />}
                  />
                }
              >
                <Route path="create-template" element={<CreateLifeCycle />} />
                <Route path="edit-template/:id" element={<CreateLifeCycle />} />
              </Route>
            </Route>
            {/*************** SETTINGS / AUTOMATION / Auto assignments ************** */}

            <Route
              path="automation/auto-assignment"
              element={
                <RouteGuard
                  moduleId="auto_assignment_rules"
                  element={<BlankLayout documentTitle="Assignment Rules" />}
                />
              }
            >
              {" "}
              <Route path="rules" element={<AutoAssignmentTabs />}>
                <Route path="contacts" element={<ContactsListing />} />
                <Route path="create" element={<CreateContacts />} />
                <Route path="edit/:id" element={<CreateContacts />} />
              </Route>
            </Route>

            {/*************** SETTINGS / CHAT SETTINGS ************** */}
            <Route
              path="chat-settings"
              element={
                <RouteGuard
                  moduleId="chat_settings"
                  element={<BlankLayout documentTitle="Chat Settings" />}
                />
              }
            >
              <Route
                path="assignment"
                element={
                  <RouteGuard
                    moduleId="chat_assignment"
                    element={<ChatAssignment />}
                  />
                }
              />
              <Route
                path="labels"
                element={
                  <RouteGuard
                    moduleId="manage_conversation_labels"
                    element={<ChatLabels />}
                  />
                }
              />
              <Route
                path="canned-response"
                element={
                  <RouteGuard
                    moduleId="quick_replies"
                    element={<CannedResponse />}
                  />
                }
              />
              <Route
                path="business-hours"
                element={
                  <RouteGuard
                    moduleId="manage_business_hours"
                    element={<BusinessHours />}
                  />
                }
              />
              <Route
                path="inactivity-timeouts"
                element={
                  <RouteGuard
                    moduleId="inactivity_timeouts"
                    element={<InactivityTimeout />}
                  />
                }
              />
            </Route>
            {/*************** SETTINGS / CHAT SETTINGS ************** */}
            <Route
              path="chat-settings"
              element={
                <RouteGuard
                  moduleId="chat_settings"
                  element={<BlankLayout documentTitle="Chat Settings" />}
                />
              }
            >
              <Route
                path="assignment"
                element={
                  <RouteGuard
                    moduleId="chat_assignment"
                    element={<ChatAssignment />}
                  />
                }
              />
              <Route
                path="labels"
                element={
                  <RouteGuard
                    moduleId="manage_conversation_labels"
                    element={<ChatLabels />}
                  />
                }
              />
              <Route
                path="canned-response"
                element={
                  <RouteGuard
                    moduleId="manage_canned_responses"
                    element={<CannedResponse />}
                  />
                }
              />
              <Route
                path="business-hours"
                element={
                  <RouteGuard
                    moduleId="manage_business_hours"
                    element={<BusinessHours />}
                  />
                }
              />
              <Route
                path="inactivity-timeouts"
                element={
                  <RouteGuard
                    moduleId="inactivity_timeouts"
                    element={<InactivityTimeout />}
                  />
                }
              />
              <Route
                path="opt-in-Management"
                element={
                  <RouteGuard
                    moduleId="user_consent"
                    element={<OptInManagement />}
                  />
                }
              />
            </Route>

            {/*************** SETTINGS / MANAGE PROFILE ************** */}
            <Route
              path="manage-account"
              element={
                <RouteGuard moduleId="manage_account" element={<BlankLayout />} />
              }
            >
              <Route
                path="whatsapp-profile"
                element={
                  <RouteGuard
                    moduleId="manage_profile_whatsapp"
                    element={<WhatsappManageProfile />}
                  />
                }
              />
              <Route
                path="sub-accounts"
                element={
                  <RouteGuard
                    moduleId="manage_users"
                    element={<BlankLayout />}
                  />
                }
              >
                <Route path="" element={<SubAccountsLiting/>}/>
                <Route path="transaction-logs" element={<TransactionalLogs/>}/>
                <Route path="own-transaction-logs" element={<OwnTransactionalLogs/>}/>
              </Route>
            </Route>
            <Route
              path="manage-account"
              element={
                <RouteGuard
                  moduleId="manage_account"
                  element={<BlankLayout documentTitle="Manage Profile" />}
                />
              }
            >
              <Route
                path="webhooks"
                element={
                  <RouteGuard moduleId="webhooks" element={<Webhook />} />
                }
              />
            </Route>
            <Route
              path="manage-account"
              element={
                <RouteGuard
                  moduleId="manage_account"
                  element={<BlankLayout documentTitle="Manage Profile" />}
                />
              }
            >
              <Route
                path="user-profile"
                element={
                  <RouteGuard
                    moduleId="webhooks"
                    element={<Webhook />}
                  />
                }
              />
            </Route>
            <Route
              path="manage-account"
              element={
                <RouteGuard
                  moduleId="manage_account"
                  element={<BlankLayout documentTitle="Manage Profile" />}
                />
              }
            >
              <Route
                path="user-profile"
                element={
                  <RouteGuard
                    moduleId="webhooks"
                    element={<UserProfile />}
                  />
                }
              />
            </Route>
            <Route
              path="manage-account"
              element={
                <RouteGuard
                  moduleId="manage_account"
                  element={<BlankLayout documentTitle="Manage Profile" />}
                />
              }
            >
              <Route
                path="api-keys"
                element={
                  <RouteGuard
                    moduleId="api_keys"
                    element={<APIKeyGeneration />}
                  />
                }
              />
            </Route>
          </Route>

          {/*************** SUBSCRIPTION ************** */}
          <Route
            path="/subscription"
            element={
              <RouteGuard
                moduleId="subscriptions"
                element={
                  <SidebarLayoutWrapper
                    menuOptionsKeyName="subscription_sidebar_menu"
                    menuDisplayType={"menu"}
                    sidebarTitle={"Subscription"}
                    documentTitle="Subscription"
                  />
                }
              />
            }
          >
            <Route
              index
              element={
                <HomeRouteIndex
                  dynamicMenuKeyName="subscription_sidebar_menu"
                  routeIndexPath="/subscription"
                />
              }
            />
            <Route path="track-expenses" element={<TrackingExpenses />} />
            <Route path="billing-summary" element={<BillingSummary />} />
            <Route
              path="billing-summary/whatsapp"
              element={<WhatsappBillingLogs />}
            />
            <Route path="subscription-plans" element={<Subscription />} />
            <Route
              path="subscription-plans/details"
              element={<PlanDetails />}
            />
            <Route path="subscription-plans/plans" element={<Plans />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="wallet-invoices" element={<WallectInvoices />} />
            <Route path="success" element={<SubscriptionSuccess />} />
          </Route>

          <Route
            path="/task"
            element={<RouteGuard moduleId="task" element={<BlankLayout />} />}
          >
            <Route
              path="lists/:id?"
              element={
                <RouteGuard moduleId="task" element={<AllTaskScreen />} />
              }
            />
          </Route>

          {/*************** BOT BUILDER ************** */}
          <Route
            path="/workspace"
            element={
              <RouteGuard
                moduleId="bot_builder"
                element={<BlankLayout documentTitle="Bot builder" />}
              />
            }
          >
            <Route path="" element={<FreshStartPage />} />

            <Route path="create" element={<CreateWorkspace />} />

            <Route path=":workspace_id" element={<FreshStartPage />} />
            <Route path=":workspace_id/setup/bots" element={<AllChatbots />} />
            <Route
              path=":workspace_id/setup/create-bot"
              element={
                <RouteGuard moduleId="create_bot" element={<CreateBotForm />} />
              }
            />
            <Route
              path=":workspace_id/setup/edit-bot/:bot_id"
              element={
                <RouteGuard moduleId="manage_bot" element={<CreateBotForm />} />
              }
            />
            <Route
              path=":workspace_id/setup/view-bot/:bot_id"
              element={
                <RouteGuard moduleId="manage_bot" element={<CreateBotForm />} />
              }
            />
          </Route>

          <Route
            path="/bot/:bot_id/setup"
            element={
              <RouteGuard moduleId="manage_bot" element={<BotBuilder />} />
            }
          >
            <Route
              path="flow"
              element={
                <RouteGuard moduleId="flow_builder" element={<BlankLayout />} />
              }
            >
              <Route path="" element={<FlowBuilderV2 />} />
              <Route path=":flow_id" element={<FlowBuilderV2 />} />
            </Route>

            <Route
              path="bot-knowledge"
              element={
                <RouteGuard
                  moduleId="manage_bot_knowledgebase"
                  element={<BotKnowledge />}
                />
              }
            >
              <Route
                path="sources"
                element={
                  <RouteGuard moduleId="smart_ai" element={<FaqIndex />} />
                }
              >
                <Route path="website" element={<FaqWebsite />} />
                <Route path="sitemap" element={<FaqSitemap />} />
                <Route path="document" element={<FaqPdf />} />
                <Route path="text" element={<FaqText />} />
              </Route>

              <Route
                path="user-queries"
                element={
                  <RouteGuard
                    moduleId="user_queries"
                    element={<NotAnsweredIndex />}
                  />
                }
              >
                <Route path="not-answered" element={<NotAnswered />} />
                <Route path="answered" element={<AnsweredFaq />} />
              </Route>
              <Route path="faqs" element={<BlankLayout />}>
                <Route index path="" element={<FaqBuilder />} />
                <Route path="faq-answer" element={<FaqDetails />} />
                <Route path="faq-answer/:faq_id" element={<FaqDetails />} />
              </Route>

              <Route path="smart-ai" element={<SmartAi />} />
            </Route>
            <Route
              path="configure"
              element={
                <RouteGuard moduleId="bot_settings" element={<BotSettings />} />
              }
            >
              <Route
                path="look-feel"
                element={
                  <RouteGuard moduleId="customize" element={<LookFeel />} />
                }
              />
              <Route
                path="persistant-menu"
                element={
                  <RouteGuard
                    moduleId="customize"
                    element={<PersistantMenu />}
                  />
                }
              />
              <Route
                path="popout-message"
                element={
                  <RouteGuard
                    moduleId="customize"
                    element={<PopoutMessage />}
                  />
                }
              />
              <Route
                path="link-qrcode"
                element={
                  <RouteGuard
                    moduleId="customize"
                    element={<LinkAndQRCode />}
                  />
                }
              />
              <Route path="enabled-devices" element={<EnabledDevices />} />
              <Route path="auto-replies" element={<AutoReplies />} />
              <Route
                path="website"
                element={
                  <RouteGuard
                    moduleId="deploy_bot"
                    element={<DeployScripts />}
                  />
                }
              />
            </Route>
          </Route>
          {/* CTWA */}
          <Route
            path="/ads"
            element={
              <RouteGuard moduleId="automation" element={<BlankLayout />} />
            }
          >
            <Route path="ctwa/setup" element={<CTWASetup />} />
            <Route path="ctwa/ads-list" element={<CTWAAdsList />} />
          </Route>

          {/* <Route
            path="/ecommerce"
            element={<RouteGuard moduleId="contacts" element={<BlankLayout />} />}
          >
            <Route path="setup" element={<CatalogSetup />} />
            <Route path="code" element={<CatalogCode />} />

          </Route> */}

          {/*************** AppStore ************** */}
          <Route path="/app-store">
            <Route path="" element={<AppStore />} />
            <Route path="app-detail/:id" element={<AppDetail />} />
            <Route path="install/:appname" element={<AppConfig />} />
            <Route path="redirect" element={<RedirectPage />} />
            <Route path="shopify/redirect" element={<ShopifyRedirectPage />} />
          </Route>
          {/*************** AppStore ************** */}

          {/*************** ERROR PAGE ************** */}
          <Route path="/404" element={<App404 />} />

          {/*************** VEIRFY PAGE************** */}
          <Route path="/verify" element={<EmailValidation />} />

          <Route
          path="api/library"
          element={
            <RouteGuard moduleId="api_library" element={<BlankLayout />} />
          }
        >
          <Route path="" element={<ApiLibraryList />} />
          <Route path="create" element={<CreateApi />} />
          <Route path="edit/:id" element={<CreateApi />} />
          
        </Route>

          {/*************** FORBIDDEN / ACCESS DENIED************** */}
          <Route path="/forbidden" element={<ProtectedAccessDeniedPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
};

export default ProtectedRoutes;
